import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    ArrowLeft,
    Save,
    User,
    Building,
    Mail,
    Phone,
    FileText,
    MapPin,
    CheckCircle,
    XCircle,
    AlertCircle,
    Upload,
    X
} from 'lucide-react';
import customerService from '../../../api/crm/customers/customerService';
import {
    sanitizeTextInput,
    sanitizeEmailInput,
    sanitizeAddressInput,
    validatePhoneInput,
    validateEmailInput,
    validateRequiredFields,
    getCharacterCount,
    FIELD_LIMITS
} from '../../../utils/inputValidation';
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from '../../../utils/formValidation';
import { toast } from 'react-hot-toast';
import { Country, State, City } from 'country-state-city';
import LocationSelect from '../../../components/common/LocationSelect/LocationSelect';

const CustomerForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isEditMode = !!id;
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        profileImage: null,
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        alternatePhone: '',
        website: '',
        industry: '',
        companySize: '',
        annualRevenue: '',
        region: '',
        decisionMaker: '',
        gstNumber: '',
        drugLicenseNumber: '',
        businessType: 'other',
        source: 'direct',
        status: 'customer',
        priority: 'medium',
        creditLimit: 0,
        paymentTerms: 'credit_30',
        preferredChannel: 'email',
        tags: [],
        isActive: true,
        billingAddress: {
            street: '',
            city: null,
            state: null,
            pincode: '',
            country: { name: 'India', isoCode: 'IN' }
        },
        shippingAddress: {
            street: '',
            city: null,
            state: null,
            pincode: '',
            country: { name: 'India', isoCode: 'IN' }
        }
    });

    const [previewImage, setPreviewImage] = useState(null);

    const [shippingSameAsBilling, setShippingSameAsBilling] = useState(!isEditMode);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // Location options states
    const [billingCountryOptions, setBillingCountryOptions] = useState([]);
    const [billingStateOptions, setBillingStateOptions] = useState([]);
    const [billingCityOptions, setBillingCityOptions] = useState([]);

    const [shippingCountryOptions, setShippingCountryOptions] = useState([]);
    const [shippingStateOptions, setShippingStateOptions] = useState([]);
    const [shippingCityOptions, setShippingCityOptions] = useState([]);

    // Load countries on mount
    useEffect(() => {
        const allCountries = Country.getAllCountries().map(c => ({
            id: c.isoCode,
            name: c.name,
            isoCode: c.isoCode,
            ...c
        }));
        setBillingCountryOptions(allCountries);
        setShippingCountryOptions(allCountries);
    }, []);

    // Sync Billing States
    useEffect(() => {
        if (formData.billingAddress.country?.isoCode) {
            const states = State.getStatesOfCountry(formData.billingAddress.country.isoCode).map(s => ({
                id: s.isoCode,
                name: s.name,
                isoCode: s.isoCode,
                countryCode: s.countryCode,
                ...s
            }));
            setBillingStateOptions(states);
        } else {
            setBillingStateOptions([]);
        }
    }, [formData.billingAddress.country]);

    // Sync Billing Cities
    useEffect(() => {
        if (formData.billingAddress.country?.isoCode && formData.billingAddress.state?.isoCode) {
            const cities = City.getCitiesOfState(formData.billingAddress.country.isoCode, formData.billingAddress.state.isoCode).map(c => ({
                id: `${c.name}-${c.stateCode}`,
                name: c.name,
                stateCode: c.stateCode,
                countryCode: c.countryCode,
                ...c
            }));
            setBillingCityOptions(cities);
        } else {
            setBillingCityOptions([]);
        }
    }, [formData.billingAddress.country, formData.billingAddress.state]);

    // Sync Shipping States
    useEffect(() => {
        if (formData.shippingAddress.country?.isoCode) {
            const states = State.getStatesOfCountry(formData.shippingAddress.country.isoCode).map(s => ({
                id: s.isoCode,
                name: s.name,
                isoCode: s.isoCode,
                countryCode: s.countryCode,
                ...s
            }));
            setShippingStateOptions(states);
        } else {
            setShippingStateOptions([]);
        }
    }, [formData.shippingAddress.country]);

    // Sync Shipping Cities
    useEffect(() => {
        if (formData.shippingAddress.country?.isoCode && formData.shippingAddress.state?.isoCode) {
            const cities = City.getCitiesOfState(formData.shippingAddress.country.isoCode, formData.shippingAddress.state.isoCode).map(c => ({
                id: `${c.name}-${c.stateCode}`,
                name: c.name,
                stateCode: c.stateCode,
                countryCode: c.countryCode,
                ...c
            }));
            setShippingCityOptions(cities);
        } else {
            setShippingCityOptions([]);
        }
    }, [formData.shippingAddress.country, formData.shippingAddress.state]);

    // Fetch customer data if in edit mode
    const { data: customerData, isLoading: isFetching } = useQuery({
        queryKey: ['customer', id],
        queryFn: async () => {
            if (!id) return null;
            const result = await customerService.getCustomerById(id);
            if (result.success) return result.customer;
            throw new Error(result.error);
        },
        enabled: isEditMode
    });

    // Fetch industries configuration
    const { data: industriesData, isLoading: isLoadingIndustries } = useQuery({
        queryKey: ['industries'],
        queryFn: async () => {
            const result = await customerService.getIndustries();
            if (result.success) return result.data;
            return [];
        }
    });

    // Fetch decision makers configuration
    const { data: decisionMakersData, isLoading: isLoadingDecisionMakers } = useQuery({
        queryKey: ['decisionMakers'],
        queryFn: async () => {
            const result = await customerService.getDecisionMakers();
            if (result.success) return result.data;
            return [];
        }
    });

    // Fetch company sizes configuration
    const { data: companySizesData, isLoading: isLoadingCompanySizes } = useQuery({
        queryKey: ['companySizes'],
        queryFn: async () => {
            const result = await customerService.getCompanySizes();
            if (result.success) return result.data;
            return [];
        }
    });

    // Populate form data when customer data is fetched
    useEffect(() => {
        if (customerData) {
            // Support both direct customer object and nested customer object (like in 360 detail)
            const customer = customerData.customer || customerData;

            // Helper function to map company size value to dropdown option
            const mapCompanySizeToDropdown = (sizeValue) => {
                if (!sizeValue) return '';
                
                const masterSizes = companySizesData?.map(s => s.name) || [];
                if (masterSizes.includes(sizeValue)) return sizeValue;

                // Parse number from string like "200 employees"
                const match = sizeValue.match(/\d+/);
                if (match) {
                    const count = parseInt(match[0]);
                    // Fallback to closest match if possible, or just return as is if matched by master data later
                    // For now, if it's not in master data, try to match common patterns
                    if (count <= 10 && masterSizes.includes('Startup (1-10 employees)')) return 'Startup (1-10 employees)';
                    if (count <= 50 && masterSizes.includes('Small (10-50 employees)')) return 'Small (10-50 employees)';
                    if (count <= 200 && masterSizes.includes('Medium (50-200 employees)')) return 'Medium (50-200 employees)';
                    if (count <= 1000 && masterSizes.includes('Large (200-1000 employees)')) return 'Large (200-1000 employees)';
                    if (count > 1000 && masterSizes.includes('Enterprise (1000+ employees)')) return 'Enterprise (1000+ employees)';
                }
                
                // Final check: case insensitive match
                const caseInsensitiveMatch = masterSizes.find(s => s.toLowerCase() === sizeValue.toLowerCase());
                return caseInsensitiveMatch || '';
            };

            // Helper function to capitalize first letter for industry
            const capitalizeIndustry = (industry) => {
                if (!industry) return '';
                return industry.charAt(0).toUpperCase() + industry.slice(1);
            };

            setFormData({
                profileImage: customer.profileImage || null,
                companyName: customer.companyName || '',
                contactPerson: customer.contactPerson || '',
                email: customer.email || '',
                phone: Array.isArray(customer.phone) ? (customer.phone[0] || '') : (customer.phone || ''),
                alternatePhone: Array.isArray(customer.phone) ? (customer.phone[1] || '') : '',
                website: customer.website || '',
                industry: capitalizeIndustry(customer.industry) || '',
                companySize: mapCompanySizeToDropdown(customer.companySize) || '',
                annualRevenue: customer.annualRevenue ? String(customer.annualRevenue) : '',
                region: customer.region || '',
                decisionMaker: customer.decisionMaker || '',
                gstNumber: customer.gstNumber || customer.gstin || '',
                drugLicenseNumber: customer.drugLicenseNumber || customer.drugLicense || '',
                businessType: customer.businessType || 'other',
                source: customer.source || 'direct',
                status: customer.status || 'customer',
                priority: customer.priority || 'medium',
                creditLimit: customer.creditLimit || 0,
                paymentTerms: customer.paymentTerms || 'credit_30',
                preferredChannel: customer.preferredChannel || 'email',
                tags: customer.tags || [],
                isActive: customer.isActive !== false,
                billingAddress: {
                    street: customer.billingAddress?.street || '',
                    city: customer.billingAddress?.city?.name ? customer.billingAddress.city : (customer.billingAddress?.city ? { name: customer.billingAddress.city } : null),
                    state: customer.billingAddress?.state?.name ? customer.billingAddress.state : (customer.billingAddress?.state ? { name: customer.billingAddress.state } : null),
                    pincode: customer.billingAddress?.pincode || '',
                    country: customer.billingAddress?.country?.isoCode ? customer.billingAddress.country : (customer.billingAddress?.country ? { name: customer.billingAddress.country, isoCode: 'IN' } : { name: 'India', isoCode: 'IN' })
                },
                shippingAddress: {
                    street: customer.shippingAddress?.street || '',
                    city: customer.shippingAddress?.city?.name ? customer.shippingAddress.city : (customer.shippingAddress?.city ? { name: customer.shippingAddress.city } : null),
                    state: customer.shippingAddress?.state?.name ? customer.shippingAddress.state : (customer.shippingAddress?.state ? { name: customer.shippingAddress.state } : null),
                    pincode: customer.shippingAddress?.pincode || '',
                    country: customer.shippingAddress?.country?.isoCode ? customer.shippingAddress.country : (customer.shippingAddress?.country ? { name: customer.shippingAddress.country, isoCode: 'IN' } : { name: 'India', isoCode: 'IN' })
                }
            });

            if (customer.profileImage && !previewImage) {
                const preview = typeof customer.profileImage === 'string'
                    ? customer.profileImage
                    : (customer.profileImage.fileUrl || customer.profileImage.url);
                if (preview) setPreviewImage(preview);
            }

            // Attempt to resolve Country/State/City objects if they are just strings (handled minimally above)
            // A more robust solution would be to find them in the options lists, but that requires options to be loaded.
            // Since we set options on mount, we might be able to refine them here if needed, but for now simple structure {name: ...} works for display.


            // Check if shipping and billing are the same
            if (customer.billingAddress && customer.shippingAddress) {
                const isSame = JSON.stringify(customer.billingAddress) === JSON.stringify(customer.shippingAddress);
                setShippingSameAsBilling(isSame);
            }
        }
    }, [customerData]);

    // Sync shipping with billing if checkbox is checked
    useEffect(() => {
        if (shippingSameAsBilling) {
            setFormData(prev => ({
                ...prev,
                shippingAddress: { ...prev.billingAddress }
            }));
        }
    }, [shippingSameAsBilling, formData.billingAddress]);

    const handleFieldChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 255) => {
        let processedValue = value;
        let error = '';

        if (fieldType === 'select') {
            // For select/dropdown, use value directly without any processing
            processedValue = value;
        } else if (fieldType === 'url') {
            // For URL fields, allow all valid URL characters
            // Valid URL characters: A-Z a-z 0-9 - . _ ~ : / ? # [ ] @ ! $ & ' ( ) * + , ; = %
            processedValue = String(value || '').slice(0, maxLength);
        } else if (fieldType === 'text') {
            processedValue = String(value || '').replace(INVALID_CHARS_REGEX, '');
            processedValue = processedValue.slice(0, maxLength);
        } else if (fieldType === 'phone') {
            const phoneValidation = validatePhoneInput(value);
            processedValue = phoneValidation.value;
            error = phoneValidation.error || '';
        } else if (fieldType === 'email') {
            processedValue = sanitizeEmailInput(String(value || ''));
            processedValue = processedValue.slice(0, maxLength);
            if (processedValue) {
                const emailValidation = validateEmailInput(processedValue);
                if (!emailValidation.isValid) {
                    error = emailValidation.error;
                }
            }
        } else if (fieldType === 'address') {
            processedValue = String(value || '').replace(INVALID_CHARS_REGEX, '');
            processedValue = processedValue.slice(0, maxLength);
        } else if (fieldType === 'number') {
            // Only allow numbers
            processedValue = String(value || '').replace(/\D/g, '');
            processedValue = processedValue.slice(0, maxLength);
        }

        // Handle nested paths like 'billingAddress.city'
        if (field.includes('.')) {
            const [parent, child] = field.split('.');

            // If user edits shipping address manually, uncheck "Copy from billing"
            if (parent === 'shippingAddress' && shippingSameAsBilling) {
                setShippingSameAsBilling(false);
            }
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: processedValue
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: processedValue
            }));
        }

        // Set error if validation failed
        if (error) {
            setErrors(prev => ({
                ...prev,
                [field]: error
            }));
        } else if (errors[field]) {
            // Clear error if validation passed
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleProfileImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a valid image file (JPG, PNG, GIF, or WEBP)');
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.error('Image size must be less than 5MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const imageData = {
                file: file, // Store the raw file object
                fileUrl: event.target.result,
                fileType: file.type,
                fileSize: file.size,
                originalName: file.name,
                uploadedAt: new Date()
            };
            setFormData(prev => ({ ...prev, profileImage: imageData }));
            setPreviewImage(event.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleBlur = (field) => {
        const value = formData[field];
        let errorMessage = '';

        // Check required fields
        if (['contactPerson', 'phone'].includes(field)) {
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                const fieldLabel = field === 'contactPerson' ? 'Customer Name' : field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
                errorMessage = `${fieldLabel} is required`;
            }
        }

        // Check format validations if value exists
        if (value && !errorMessage) {
            if (field === 'email') {
                const emailValidation = validateEmailInput(value);
                if (!emailValidation.isValid) {
                    errorMessage = emailValidation.error;
                }
            } else if (field === 'phone') {
                const phoneValidation = validatePhoneInput(value);
                if (!phoneValidation.isValid) {
                    errorMessage = phoneValidation.error || 'Phone number must be 10 digits';
                }
            }
        }

        if (errorMessage) {
            setErrors(prev => ({
                ...prev,
                [field]: errorMessage
            }));
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const removeProfileImage = () => {
        setFormData(prev => ({ ...prev, profileImage: null }));
        setPreviewImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const validateFormData = () => {
        const newErrors = {};

        // Validate required fields
        const requiredFields = ['contactPerson', 'phone'];
        requiredFields.forEach(field => {
            const value = formData[field];
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                const fieldLabel = field === 'contactPerson' ? 'Customer Name' : field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
                newErrors[field] = `${fieldLabel} is required`;
            }
        });

        // Regex validation for names and descriptive fields
        const textFieldsToValidate = [
            { key: 'contactPerson', label: 'Customer Name' },
            { key: 'companyName', label: 'Company Name' }
        ];

        textFieldsToValidate.forEach(field => {
            if (formData[field.key] && !VALID_TEXT_REGEX.test(formData[field.key])) {
                newErrors[field.key] = `${field.label} contains invalid characters`;
            }
        });

        // Validate address fields
        if (formData.billingAddress.street && !VALID_TEXT_REGEX.test(formData.billingAddress.street)) {
            newErrors['billingAddress.street'] = 'Street address contains invalid characters';
        }

        if (!shippingSameAsBilling && formData.shippingAddress.street && !VALID_TEXT_REGEX.test(formData.shippingAddress.street)) {
            newErrors['shippingAddress.street'] = 'Street address contains invalid characters';
        }

        if (formData.billingAddress.pincode && !VALID_TEXT_REGEX.test(formData.billingAddress.pincode)) {
            newErrors['billingAddress.pincode'] = 'Postal code contains invalid characters';
        }

        if (!shippingSameAsBilling && formData.shippingAddress.pincode && !VALID_TEXT_REGEX.test(formData.shippingAddress.pincode)) {
            newErrors['shippingAddress.pincode'] = 'Postal code contains invalid characters';
        }

        // Validate email format if provided
        if (formData.email) {
            const emailValidation = validateEmailInput(formData.email);
            if (!emailValidation.isValid) {
                newErrors.email = emailValidation.error;
            }
        }

        // Validate phone number (must be 10 digits)
        if (formData.phone) {
            const phoneValidation = validatePhoneInput(formData.phone);
            if (!phoneValidation.isValid) {
                newErrors.phone = phoneValidation.error || 'Phone number must be 10 digits';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);

        // Validate form data
        if (!validateFormData()) {
            toast.error('Please fill all the required fields');
            return;
        }

        setIsSubmitting(true);
        try {
            const formattedBilling = {
                street: formData.billingAddress.street || 'Not provided',
                city: formData.billingAddress.city || { name: 'Not provided' },
                state: formData.billingAddress.state || { name: 'Not provided' },
                country: formData.billingAddress.country || { name: 'India', isoCode: 'IN' },
                pincode: formData.billingAddress.pincode || '000000'
            };

            const formattedShipping = {
                street: formData.shippingAddress.street || 'Not provided',
                city: formData.shippingAddress.city || { name: 'Not provided' },
                state: formData.shippingAddress.state || { name: 'Not provided' },
                country: formData.shippingAddress.country || { name: 'India', isoCode: 'IN' },
                pincode: formData.shippingAddress.pincode || '000000'
            };

            const phoneArray = [formData.phone, formData.alternatePhone].map(p => p?.trim()).filter(Boolean);

            const baseData = {
                ...formData,
                phone: phoneArray, // Store as array
                billingAddress: formattedBilling,
                shippingAddress: shippingSameAsBilling ? formattedBilling : formattedShipping
            };

            // Cleanup internal state before sending if needed (optional)
            delete baseData.alternatePhone;

            let result;
            // Check if we have a new profile image file to upload
            if (formData.profileImage?.file) {
                const formDataToSubmit = new FormData();
                formDataToSubmit.append('profileImage', formData.profileImage.file);

                // Remove the file object and fileUrl from data we send as JSON to avoid bloat/errors
                const { profileImage, ...otherData } = baseData;
                formDataToSubmit.append('data', JSON.stringify(otherData));

                if (isEditMode) {
                    result = await customerService.updateCustomer(id, formDataToSubmit);
                } else {
                    result = await customerService.createCustomer(formDataToSubmit);
                }
            } else {
                // Regular JSON submission (if no new file)
                if (isEditMode) {
                    result = await customerService.updateCustomer(id, baseData);
                } else {
                    result = await customerService.createCustomer(baseData);
                }
            }

            if (result.success) {
                toast.success(isEditMode ? 'Customer updated successfully!' : 'Customer created successfully!');
                queryClient.invalidateQueries(['customer360List']);
                if (isEditMode) {
                    queryClient.invalidateQueries(['customer', id]);
                    queryClient.invalidateQueries(['customer360', id]);
                }
                setTimeout(() => {
                    navigate('/customer_360');
                }, 100);
            } else {
                setSubmitError(result.error || 'Something went wrong');
                toast.error(result.error || 'Something went wrong');
            }
        } catch (err) {
            setSubmitError(err.message || 'Failed to save customer');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isEditMode && isFetching) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
            </div>
        );
    }

    return (
        <div className="py-6 px-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeft className="h-6 w-6 text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            {isEditMode ? 'Edit Customer' : 'Add New Customer'}
                        </h1>
                        <p className="text-gray-600">
                            {isEditMode ? 'Update customer profile and settings' : 'Create a new customer profile'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Alert */}
                {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-3">
                        <AlertCircle className="h-5 w-5" />
                        <p>{submitError}</p>
                    </div>
                )}

                {/* Basic Information Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                        <User className="h-5 w-5 text-brand" />
                        <h2 className="font-semibold text-gray-900">Basic Information</h2>
                    </div>
                    <div className="p-6">
                        {/* Profile Image Section */}
                        <div className="mb-8 flex flex-col sm:flex-row items-center gap-6 p-4 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                            <div className="relative group">
                                <div className="h-24 w-24 rounded-full overflow-hidden bg-white border-2 border-white shadow-md ring-1 ring-gray-200">
                                    {previewImage ? (
                                        <img
                                            src={previewImage}
                                            alt="Profile Preview"
                                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                                        />
                                    ) : (
                                        <div className="h-full w-full flex items-center justify-center bg-gray-50">
                                            <User className="h-10 w-10 text-gray-300" />
                                        </div>
                                    )}
                                </div>
                                {previewImage && (
                                    <button
                                        type="button"
                                        onClick={removeProfileImage}
                                        className="absolute top-0 right-0 p-1.5 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 transition-all opacity-0 group-hover:opacity-100"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                            <div className="flex-1 text-center sm:text-left">
                                <h3 className="text-sm font-semibold text-gray-900 mb-1">Profile Image</h3>
                                <p className="text-xs text-gray-500 mb-4">Recommended: Square image, max 5MB (JPG, PNG)</p>
                                <label className="btn-cancel inline-flex items-center px-4 py-2 cursor-pointer w-auto h-auto min-h-[40px]">
                                    <Upload className="h-4 w-4 mr-2" />
                                    <span>{previewImage ? 'Change Image' : 'Upload Image'}</span>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        onChange={handleProfileImageUpload}
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Customer Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="contactPerson"
                                    value={formData.contactPerson}
                                    onChange={(e) => handleFieldChange('contactPerson', e.target.value, 'text', 100)}
                                    placeholder="Enter full name"
                                    className={`input-field w-full px-4 py-2 ${errors.contactPerson ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                    onBlur={() => handleBlur('contactPerson')}
                                />
                                <div className="flex justify-between mt-1">
                                    {errors.contactPerson ? (
                                        <p className="text-xs text-red-500">{errors.contactPerson}</p>
                                    ) : (
                                        <div />
                                    )}
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.contactPerson, 100)}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Company/Business Name
                                </label>
                                <input
                                    type="text"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={(e) => handleFieldChange('companyName', e.target.value, 'text', 100)}
                                    placeholder="Enter company name"
                                    className={`input-field w-full px-4 py-2 ${errors.companyName ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                />
                                <div className="flex justify-between mt-1">
                                    {errors.companyName ? (
                                        <p className="text-xs text-red-500">{errors.companyName}</p>
                                    ) : (
                                        <div />
                                    )}
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.companyName, 100)}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={(e) => handleFieldChange('email', e.target.value, 'email', 100)}
                                    placeholder="example@email.com"
                                    className={`input-field w-full px-4 py-2 ${errors.email ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                    onBlur={() => handleBlur('email')}
                                />
                                <div className="flex justify-between mt-1">
                                    {errors.email ? (
                                        <p className="text-xs text-red-500">{errors.email}</p>
                                    ) : (
                                        <div />
                                    )}
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.email, 100)}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={(e) => handleFieldChange('phone', e.target.value, 'phone', 10)}
                                    placeholder="Enter 10-digit number"
                                    className={`input-field w-full px-4 py-2 ${errors.phone ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                    onBlur={() => handleBlur('phone')}
                                />
                                <div className="flex justify-between mt-1">
                                    {errors.phone ? (
                                        <p className="text-xs text-red-500">{errors.phone}</p>
                                    ) : (
                                        <div />
                                    )}
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.phone, 15)}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Alternate Phone
                                </label>
                                <input
                                    type="text"
                                    name="alternatePhone"
                                    value={formData.alternatePhone}
                                    onChange={(e) => handleFieldChange('alternatePhone', e.target.value, 'phone', 10)}
                                    placeholder="Enter 10-digit number"
                                    className={`input-field w-full px-4 py-2 ${errors.alternatePhone ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                />
                                <div className="flex justify-between mt-1">
                                    {errors.alternatePhone ? (
                                        <p className="text-xs text-red-500">{errors.alternatePhone}</p>
                                    ) : (
                                        <div />
                                    )}
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.alternatePhone, 15)}
                                    </span>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* Business Information Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                        <Building className="h-5 w-5 text-brand" />
                        <h2 className="font-semibold text-gray-900">Business Information</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Website
                                </label>
                                <input
                                    type="url"
                                    name="website"
                                    value={formData.website}
                                    onChange={(e) => handleFieldChange('website', e.target.value, 'url', 100)}
                                    placeholder="https://example.com"
                                    className="input-field w-full px-4 py-2"
                                />
                                <div className="flex justify-end mt-1">
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.website, 100)}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Industry
                                </label>
                                <select
                                    name="industry"
                                    value={formData.industry}
                                    onChange={(e) => handleFieldChange('industry', e.target.value, 'select')}
                                    className="input-field w-full px-4 py-2"
                                >
                                    <option value="">Select Industry</option>
                                    {isLoadingIndustries ? (
                                        <option disabled>Loading...</option>
                                    ) : (
                                        industriesData?.filter(i => i.isActive !== false || i.name === formData.industry).map((industry) => (
                                            <option key={industry._id} value={industry.name}>
                                                {industry.name} {industry.isActive === false ? '(Inactive)' : ''}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Company Size
                                </label>
                                    <select
                                        name="companySize"
                                        value={formData.companySize}
                                        onChange={(e) => handleFieldChange('companySize', e.target.value, 'select')}
                                        className="input-field w-full px-4 py-2"
                                    >
                                        <option value="">Select Company Size</option>
                                        {isLoadingCompanySizes ? (
                                            <option disabled>Loading...</option>
                                        ) : (
                                            companySizesData?.filter(s => s.isActive !== false || s.name === formData.companySize).map((size) => (
                                                <option key={size._id} value={size.name}>
                                                    {size.name} {size.isActive === false ? '(Inactive)' : ''}
                                                </option>
                                            ))
                                        )}
                                    </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Annual Revenue (₹)
                                </label>
                                <input
                                    type="text"
                                    name="annualRevenue"
                                    value={formData.annualRevenue}
                                    onChange={(e) => handleFieldChange('annualRevenue', e.target.value, 'number', 15)}
                                    placeholder="Enter annual revenue"
                                    className="input-field w-full px-4 py-2"
                                />
                                <div className="flex justify-end mt-1">
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.annualRevenue, 15)}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    GST Number
                                </label>
                                <input
                                    type="text"
                                    name="gstNumber"
                                    value={formData.gstNumber}
                                    onChange={(e) => {
                                        const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                        handleFieldChange('gstNumber', val, 'text', FIELD_LIMITS.gstin);
                                    }}
                                    placeholder="Enter GST Number"
                                    className="input-field w-full px-4 py-2"
                                />
                                <div className="flex justify-end mt-1">
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.gstNumber, FIELD_LIMITS.gstin)}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Drug License Number
                                </label>
                                <input
                                    type="text"
                                    name="drugLicenseNumber"
                                    value={formData.drugLicenseNumber}
                                    onChange={(e) => {
                                        const val = e.target.value.toUpperCase().replace(/[^A-Z0-9\/-]/g, '');
                                        handleFieldChange('drugLicenseNumber', val, 'text', 30);
                                    }}
                                    placeholder="Enter Drug License Number"
                                    className="input-field w-full px-4 py-2"
                                />
                                <div className="flex justify-end mt-1">
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.drugLicenseNumber, 30)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div >

                {/* Additional Details Section */}
                < div className="bg-white rounded-xl shadow-sm border border-gray-200" >
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                        <FileText className="h-5 w-5 text-brand" />
                        <h2 className="font-semibold text-gray-900">Additional Details</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Region
                                </label>
                                <select
                                    name="region"
                                    value={formData.region}
                                    onChange={(e) => handleFieldChange('region', e.target.value, 'select')}
                                    className="input-field w-full px-4 py-2"
                                >
                                    <option value="">Select Region</option>
                                    <option value="North">North</option>
                                    <option value="South">South</option>
                                    <option value="East">East</option>
                                    <option value="West">West</option>
                                    <option value="Central">Central</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Decision Maker
                                </label>
                                <select
                                    name="decisionMaker"
                                    value={formData.decisionMaker}
                                    onChange={(e) => handleFieldChange('decisionMaker', e.target.value, 'select')}
                                    className="input-field w-full px-4 py-2"
                                >
                                    <option value="">Select Decision Maker</option>
                                    {isLoadingDecisionMakers ? (
                                        <option disabled>Loading...</option>
                                    ) : (
                                        decisionMakersData?.filter(dm => dm.isActive !== false || dm.name === formData.decisionMaker).map((dm) => (
                                            <option key={dm._id} value={dm.name}>
                                                {dm.name} {dm.isActive === false ? '(Inactive)' : ''}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                        </div>
                    </div>
                </div >

                {/* Address Section */}
                < div className="grid grid-cols-1 md:grid-cols-2 gap-6" >
                    {/* Billing Address */}
                    < div className="bg-white rounded-xl shadow-sm border border-gray-200" >
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                            <MapPin className="h-5 w-5 text-brand" />
                            <h2 className="font-semibold text-gray-900">Billing Address</h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Street Address
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="billingAddress.street"
                                            value={formData.billingAddress.street}
                                            onChange={(e) => handleFieldChange('billingAddress.street', e.target.value, 'address', 100)}
                                            placeholder="Street, building, area"
                                            className={`input-field w-full px-3 py-2 ${errors['billingAddress.street'] ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                        />
                                        {errors['billingAddress.street'] && (
                                            <p className="text-xs text-red-500 mt-1">{errors['billingAddress.street']}</p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Country
                                    </label>
                                    <LocationSelect
                                        options={billingCountryOptions}
                                        value={formData.billingAddress.country}
                                        onChange={(country) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                billingAddress: {
                                                    ...prev.billingAddress,
                                                    country: country,
                                                    state: null,
                                                    city: null
                                                }
                                            }));
                                        }}
                                        placeholder="Select Country"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        State
                                    </label>
                                    <LocationSelect
                                        options={billingStateOptions}
                                        value={formData.billingAddress.state}
                                        disabled={!formData.billingAddress.country}
                                        onChange={(state) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                billingAddress: {
                                                    ...prev.billingAddress,
                                                    state: state,
                                                    city: null
                                                }
                                            }));
                                        }}
                                        placeholder="Select State"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        City
                                    </label>
                                    <LocationSelect
                                        options={billingCityOptions}
                                        value={formData.billingAddress.city}
                                        disabled={!formData.billingAddress.state}
                                        onChange={(city) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                billingAddress: {
                                                    ...prev.billingAddress,
                                                    city: city
                                                }
                                            }));
                                        }}
                                        placeholder="Select City"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Postal Code
                                    </label>
                                    <input
                                        type="text"
                                        name="billingAddress.pincode"
                                        value={formData.billingAddress.pincode}
                                        onChange={(e) => handleFieldChange('billingAddress.pincode', e.target.value, 'text', 15)}
                                        placeholder="Postal Code"
                                        maxLength={15}
                                        className={`input-field w-full px-3 py-2 ${errors['billingAddress.pincode'] ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                    />
                                    {errors['billingAddress.pincode'] && (
                                        <p className="text-xs text-red-500 mt-1">{errors['billingAddress.pincode']}</p>
                                    )}
                                    <div className="flex justify-end mt-1">
                                        <p className="text-xs text-gray-500">
                                            {(formData.billingAddress.pincode || '').length}/15 chars
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Shipping Address */}
                    < div className="bg-white rounded-xl shadow-sm border border-gray-200" >
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <MapPin className="h-5 w-5 text-brand" />
                                <h2 className="font-semibold text-gray-900">Shipping Address</h2>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={shippingSameAsBilling}
                                    onChange={(e) => setShippingSameAsBilling(e.target.checked)}
                                    className="rounded border-gray-300 text-brand focus:ring-brand h-4 w-4"
                                />
                                <span className="text-xs font-medium text-gray-600">Copy from billing address</span>
                            </label>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Street Address
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="shippingAddress.street"
                                            value={formData.shippingAddress.street}
                                            onChange={(e) => handleFieldChange('shippingAddress.street', e.target.value, 'address', 100)}
                                            placeholder="Street, building, area"
                                            className={`input-field w-full px-3 py-2 ${errors['shippingAddress.street'] ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                        />
                                        {errors['shippingAddress.street'] && (
                                            <p className="text-xs text-red-500 mt-1">{errors['shippingAddress.street']}</p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Country
                                    </label>
                                    <LocationSelect
                                        options={shippingCountryOptions}
                                        value={formData.shippingAddress.country}
                                        onChange={(country) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                shippingAddress: {
                                                    ...prev.shippingAddress,
                                                    country: country,
                                                    state: null,
                                                    city: null
                                                }
                                            }));
                                        }}
                                        placeholder="Select Country"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        State
                                    </label>
                                    <LocationSelect
                                        options={shippingStateOptions}
                                        value={formData.shippingAddress.state}
                                        disabled={!formData.shippingAddress.country}
                                        onChange={(state) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                shippingAddress: {
                                                    ...prev.shippingAddress,
                                                    state: state,
                                                    city: null
                                                }
                                            }));
                                        }}
                                        placeholder="Select State"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        City
                                    </label>
                                    <LocationSelect
                                        options={shippingCityOptions}
                                        value={formData.shippingAddress.city}
                                        disabled={!formData.shippingAddress.state}
                                        onChange={(city) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                shippingAddress: {
                                                    ...prev.shippingAddress,
                                                    city: city
                                                }
                                            }));
                                        }}
                                        placeholder="Select City"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Postal Code
                                    </label>
                                    <input
                                        type="text"
                                        name="shippingAddress.pincode"
                                        value={formData.shippingAddress.pincode}
                                        onChange={(e) => handleFieldChange('shippingAddress.pincode', e.target.value, 'text', 15)}
                                        placeholder="Postal Code"
                                        maxLength={15}
                                        className={`input-field w-full px-3 py-2 ${errors['shippingAddress.pincode'] ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                    />
                                    {errors['shippingAddress.pincode'] && (
                                        <p className="text-xs text-red-500 mt-1">{errors['shippingAddress.pincode']}</p>
                                    )}
                                    <div className="flex justify-end mt-1">
                                        <p className="text-xs text-gray-500">
                                            {(formData.shippingAddress.pincode || '').length}/15 chars
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-4">
                    <button
                        type="button"
                        onClick={() => navigate('/customer_360')}
                        className="btn-cancel px-6 h-10"
                    >
                        <span>Cancel</span>
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-create flex items-center justify-center gap-2 px-8 h-10"
                    >
                        {isSubmitting ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        ) : (
                            <Save className="h-4 w-4" />
                        )}
                        <span>{isEditMode ? 'Update Customer' : 'Create Customer'}</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CustomerForm;
